import { graphql, useStaticQuery } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import React from 'react';
import { useLayoutContext } from '../../providers/LayoutProvider';
import { DataCards, DataCardsProps } from '../DataCards/DataCards';
import { TaggedImage } from '../TaggedImage/TaggedImage';
import { TextContent } from '../TextContent/TextContent';
import './CaseStudyPreview.scss';

export const CaseStudyPreview = ({ node }) => {
    const { quizResults } = useLayoutContext();
    const {
        allNodeCaseStudyPage: { nodes: caseStudies },
    } = useStaticQuery(graphql`
        query {
            allNodeCaseStudyPage(sort: { changed: DESC }) {
                nodes {
                    fields {
                        url
                    }
                    relationships {
                        field_summary {
                            field_heading
                            field_subtitle
                            field_link {
                                title
                                uri
                            }
                            relationships {
                                field_customer {
                                    field_location
                                    relationships {
                                        field_industry {
                                            title
                                        }
                                    }
                                }
                                field_data {
                                    field_heading
                                    field_subtitle
                                    field_show_arrow
                                    field_footnote_index
                                }
                                field_image {
                                    field_media_image {
                                        alt
                                    }
                                    relationships {
                                        field_media_image {
                                            localFile {
                                                childImageSharp {
                                                    ...ImageConstrainedOneHalfSettings
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    let [match] = caseStudies;

    // ** Todo, need to add logic in here about zipcodes when those are
    // added to the data
    if (quizResults?.industry) {
        const industryMatch = caseStudies.find((caseStudy) => {
            const title =
                caseStudy.relationships?.field_summary?.relationships
                    ?.field_customer?.relationships?.field_industry?.title;

            if (title) {
                return (
                    title.toLowerCase() === quizResults.industry.toLowerCase()
                );
            }
        });

        if (industryMatch) {
            match = industryMatch;
        }
    }

    const {
        relationships: { field_summary: summary },
    } = match;

    const dataCards: DataCardsProps = {
        darkTheme: node.field_dark_mode,
        inline: true,
        size: `sm`,
        cards: summary.relationships?.field_data?.map((item) => ({
            title: item.field_heading,
            value: item.field_subtitle,
            showArrow: item.field_show_arrow,
            footnoteIndex: item.field_footnote_index,
        })),
    };

    return (
        <section
            className={`case-study-preview ${
                dataCards.darkTheme ? `dark` : `white`
            }`}
        >
            <div className="kite-container">
                <div>
                    <TaggedImage
                        bg="dark"
                        placement="left"
                        rounded={true}
                        image={getImage(
                            summary.relationships.field_image?.relationships
                                ?.field_media_image?.localFile
                        )}
                        imageAlt={
                            summary.relationships.field_image?.field_media_image
                                ?.alt
                        }
                        tag={{
                            body: {
                                icon: `ki-map-point-f`,
                                text: summary.relationships.field_customer
                                    ?.field_location,
                            },
                        }}
                    />
                </div>

                <div>
                    <TextContent
                        node={{
                            field_heading: summary.field_heading,
                            field_eyebrow_text:
                                summary.relationships?.field_customer
                                    ?.relationships?.field_industry?.title,
                            field_content_text: {
                                value: summary.field_subtitle,
                            },
                            field_link: [
                                {
                                    title: `View Case Study`,
                                    uri: match.fields?.url,
                                },
                            ],
                        }}
                    />
                    <DataCards
                        {...dataCards}
                        inline={true}
                        key={summary?.field_subtitle ?? 0}
                    />
                </div>
            </div>
        </section>
    );
};
